/**
 * Générateur — Automatisme : reconnaître en justifiant un quadrilatère, un
 * parallélogramme, un rectangle, un losange, un carré, un trapèze, un
 * pentagone, un hexagone dans des figures complexes.
 * Attendu ID : 1079 | Niveau : 5ème | Domaine : Parallélogrammes
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Quelle est la nature ?      (QCM, nature la plus précise d'un
 *     sous-quadrilatère surligné)
 *   - La bonne justification      (QCM, la nature est DONNÉE — choisir
 *     l'argument recevable ; l'allure et la règle ne le sont jamais)
 *   - Compter les côtés           (QCM, nom d'un polygone suivi sur la figure)
 *   - Vrai ou faux sur la figure  (V/F recalculé sur la figure du tirage)
 *
 * ── Plan de cartes validé (2026-09-02) ─────────────────────────────────────
 * Source : Automatisme n°41 du doc 5e + grille P1-P12 (analyse BO cycles 3-4).
 * APPUIS : toutes les figures sont nommées et définies dès le CM2 — SAUF le
 * parallélogramme quelconque, défini en début de 5e (limite de la fiche) ;
 * reconnaissance dans une figure complexe = capacité de 6e.
 * REGISTRE (P5/P10) : le QUADRILLAGE — la seule justification recevable est
 * le comptage de carreaux (lignes du quadrillage, déplacements) ; « ça a
 * l'air », la mesure à la règle ET l'équerre (revue 2026-09-02 : aucun
 * instrument ne prouve rien sur un dessin à l'écran) ne prouvent rien.
 * PROSCRIT : les propriétés des diagonales (objectif de 5e non encore
 * établi — limite de la carte n°42 voisine) ; conséquence : jamais de carré
 * incliné (indécidable par comptage sans ses diagonales).
 * OBSTACLES P11 (nommés par la fiche) : « deux côtés parallèles, donc
 * parallélogramme » (il faut DEUX paires — une seule = trapèze, question d.) ;
 * le losange pris pour un carré (les angles !) ; la confiance en l'allure.
 * INVARIANTS :
 *   - la figure complexe est GÉNÉRÉE sur quadrillage (rectangle de base,
 *     milieux, toit — trapèze isocèle ou quelconque, ou pointe : les
 *     assemblages canoniques du cycle 3, vérif. Éduscol 2026-09-02 — et
 *     quadrilatère intérieur, losange ou parallélogramme : jamais accolé au
 *     rectangle, pour ne pas mimer un pavé en perspective cavalière) ; TOUTES les natures sont RECALCULÉES
 *     par vecteurs entiers (produits en croix pour le parallélisme, produits
 *     scalaires pour les angles droits, longueurs au carré) — throw si la
 *     nature construite contredit la nature calculée ;
 *   - « nature la plus précise » dans la consigne : un carré n'est jamais
 *     défendable comme « rectangle » (convention de la fiche : nature exacte) ;
 *   - le SVG embarque les coordonnées machine (attribut data-figure) pour la
 *     re-dérivation indépendante des solveurs ;
 *   - trapèze au sens INCLUSIF ambigu : « DGHC est un parallélogramme » est
 *     posée fausse (toit toujours trapèze), « DGHC est un trapèze » vraie ;
 *   - lexique 100 % cycle 3 + « parallélogramme » (5e) ; pas de calculatrice.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux,
} from '../../../utils';
import { imgElement } from '../statistiques/svgStats';

const ATTENDU_ID = 1079;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_NATURE = 'Quelle est la nature ?';
const L_JUSTIFIER = 'La bonne justification';
const L_COMPTER = 'Compter les côtés';
const L_VF = 'Vrai ou faux sur la figure';

// ─── Vecteurs entiers : le juge des natures ────────────────────────────────

const vec = (P, Q) => [Q[0] - P[0], Q[1] - P[1]];
const dot = (u, v) => u[0] * v[0] + u[1] * v[1];
const cross = (u, v) => u[0] * v[1] - u[1] * v[0];
const len2 = (u) => dot(u, u);

/** Nature EXACTE d'un quadrilatère (sommets dans l'ordre), en arithmétique entière. */
const natureQuad = (pts) => {
    const u = [vec(pts[0], pts[1]), vec(pts[1], pts[2]), vec(pts[2], pts[3]), vec(pts[3], pts[0])];
    const par02 = cross(u[0], u[2]) === 0;
    const par13 = cross(u[1], u[3]) === 0;
    if (par02 && par13) {
        const droit = dot(u[0], u[1]) === 0;
        const egaux = len2(u[0]) === len2(u[1]);
        if (droit && egaux) return 'carre';
        if (droit) return 'rectangle';
        if (egaux) return 'losange';
        return 'parallelogramme';
    }
    return par02 || par13 ? 'trapeze' : 'quelconque';
};

const NOMS_NATURES = {
    carre: 'un carré',
    rectangle: 'un rectangle',
    losange: 'un losange',
    parallelogramme: 'un parallélogramme',
    trapeze: 'un trapèze',
};

// ─── La scène : figure complexe paramétrée sur quadrillage ─────────────────

const construireScene = () => {
    const W = randomChoice([12, 14, 16]);
    // H = W/2 une fois sur cinq : AEFD et EBCF deviennent des carrés.
    const H = randomChoice([4, 5, 6, W / 2]); // ≥ 4 : le quadrilatère intérieur garde un carreau de marge
    // Toits CANONIQUES du cycle 3 (vérif. Éduscol 2026-09-02) : trapèze ou
    // pointe (le triangle « maison »). Le toit-parallélogramme est retiré :
    // accolé au rectangle, il se lit comme un pavé en perspective cavalière.
    const toit = randomChoice(['trapeze_iso', 'trapeze_quelconque', 'pointe']);
    const k = randomChoice([2, 3]);

    const P = {
        A: [0, 0], B: [W, 0], C: [W, H], D: [0, H],
        E: [W / 2, 0], F: [W / 2, H],
    };
    if (toit === 'trapeze_iso') {
        const g = randomChoice([2, 3]);
        P.G = [g, H + k]; P.H = [W - g, H + k];
    } else if (toit === 'trapeze_quelconque') {
        const g1 = randomChoice([1, 2, 3]);
        let g2 = randomChoice([1, 2, 3].filter((x) => x !== g1));
        P.G = [g1, H + k]; P.H = [W - g2, H + k];
    } else {
        P.S = [W / 2, H + k];
    }
    // Losange intérieur (déplacements ±p, ±q), JAMAIS carré (revue 2026-09-02,
    // décision prof) : le carré incliné (p = q) n'est pas décidable par
    // comptage de carreaux sans ses diagonales (propriété proscrite), et
    // l'équerre n'est pas une preuve à l'écran (P10). p ∈ {2, 3}, q = 1.
    // Le losange reste STRICTEMENT dans la moitié gauche (W/2 ≥ 2p + 2).
    // Le parallélogramme vit ICI (déplacements « a à droite » puis « b à
    // droite, 2 en haut ») : franchement plan, aucune lecture 3D possible.
    const variante = randomChoice(['losange', 'losange', 'parallelogramme']);
    const q = 1;
    const p = randomChoice(W >= 16 ? [2, 3] : [2]);
    const a = W >= 14 ? randomChoice([3, 4]) : 3;
    const b = 1;
    if (variante === 'losange') {
        const kx = randInt(p + 1, W / 2 - p - 1);
        const ky = randInt(1, H - 2 * q - 1);
        P.K = [kx, ky]; P.L = [kx + p, ky + q]; P.M = [kx, ky + 2 * q]; P.N = [kx - p, ky + q];
    } else {
        const kx = randInt(1, W / 2 - a - b - 1);
        const ky = randInt(1, H - 3);
        P.K = [kx, ky]; P.L = [kx + a, ky]; P.M = [kx + a + b, ky + 2]; P.N = [kx + b, ky + 2];
    }

    const quads = [
        { nom: 'ABCD', pts: [P.A, P.B, P.C, P.D], attendu: 'rectangle' },
        { nom: 'AEFD', pts: [P.A, P.E, P.F, P.D], attendu: W / 2 === H ? 'carre' : 'rectangle' },
        { nom: 'EBCF', pts: [P.E, P.B, P.C, P.F], attendu: W / 2 === H ? 'carre' : 'rectangle' },
        { nom: 'KLMN', pts: [P.K, P.L, P.M, P.N], attendu: variante },
    ];
    if (toit === 'trapeze_iso' || toit === 'trapeze_quelconque') quads.push({ nom: 'DGHC', pts: [P.D, P.G, P.H, P.C], attendu: 'trapeze' });
    // AUTO-VÉRIFICATION : la nature calculée doit confirmer la construction.
    quads.forEach((qd) => {
        qd.nature = natureQuad(qd.pts);
        if (qd.nature !== qd.attendu) {
            throw new Error(`gen_1079 : ${qd.nom} construit ${qd.attendu} mais calculé ${qd.nature}`);
        }
    });

    const contour = toit === 'pointe'
        ? { noms: ['A', 'B', 'C', 'S', 'D'], pts: [P.A, P.B, P.C, P.S, P.D] }
        : { noms: ['A', 'B', 'C', 'H', 'G', 'D'], pts: [P.A, P.B, P.C, P.H, P.G, P.D] };

    return { W, H, k, p, q, a, b, variante, toit, P, quads, contour };
};

// ─── Rendu SVG : quadrillage, figure, surbrillance, données machine ────────

const rendreScene = (scene, cible) => {
    const cell = 26;
    const marge = 34;
    const xs = Object.values(scene.P).map((pt) => pt[0]);
    const ys = Object.values(scene.P).map((pt) => pt[1]);
    const xmin = Math.min(...xs) - 1; const xmax = Math.max(...xs) + 1;
    const ymin = Math.min(...ys) - 1; const ymax = Math.max(...ys) + 1;
    const X = (x) => marge + (x - xmin) * cell;
    const Y = (y) => marge + (ymax - y) * cell;
    const Wpx = marge * 2 + (xmax - xmin) * cell;
    const Hpx = marge * 2 + (ymax - ymin) * cell;

    let s = '';
    for (let x = xmin; x <= xmax; x++) s += `<line x1="${X(x)}" y1="${Y(ymin)}" x2="${X(x)}" y2="${Y(ymax)}" stroke="#e2e8f0" stroke-width="1"/>`;
    for (let y = ymin; y <= ymax; y++) s += `<line x1="${X(xmin)}" y1="${Y(y)}" x2="${X(xmax)}" y2="${Y(y)}" stroke="#e2e8f0" stroke-width="1"/>`;

    const trace = (pts, ferme = true) => {
        const d = pts.map((pt, i) => `${i === 0 ? 'M' : 'L'} ${X(pt[0])} ${Y(pt[1])}`).join(' ') + (ferme ? ' Z' : '');
        return d;
    };
    // Surbrillance de la cible (sous les traits de la figure).
    if (cible) {
        s += `<path d="${trace(cible)}" fill="#F97316" fill-opacity="0.18" stroke="#B45309" stroke-width="3"/>`;
    }
    // Les traits de la figure complexe.
    const { P } = scene;
    const segs = [[P.A, P.B], [P.B, P.C], [P.C, P.D], [P.D, P.A], [P.E, P.F]];
    if (scene.toit === 'pointe') segs.push([P.D, P.S], [P.S, P.C]);
    else segs.push([P.D, P.G], [P.G, P.H], [P.H, P.C]);
    segs.push([P.K, P.L], [P.L, P.M], [P.M, P.N], [P.N, P.K]);
    segs.forEach(([U, Vp]) => {
        s += `<line x1="${X(U[0])}" y1="${Y(U[1])}" x2="${X(Vp[0])}" y2="${Y(Vp[1])}" stroke="#334155" stroke-width="2"/>`;
    });
    // Étiquettes (décalage selon la position dans la scène).
    const offsets = {
        A: [-10, 16], B: [10, 16], C: [12, 0], D: [-12, 0], E: [0, 18], F: [8, -8],
        G: [-10, -8], H: [10, -8], S: [0, -10], K: [0, 18], L: [13, 4], M: [0, -9], N: [-13, 4],
    };
    Object.entries(scene.P).forEach(([nom, pt]) => {
        const [dx, dy] = offsets[nom] || [10, -8];
        s += `<circle cx="${X(pt[0])}" cy="${Y(pt[1])}" r="2.6" fill="#1e293b"/>`
            + `<text x="${X(pt[0]) + dx}" y="${Y(pt[1]) + dy}" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">${nom}</text>`;
    });

    // Coordonnées machine (grille, y vers le haut) pour les solveurs.
    const dataFigure = Object.entries(scene.P).map(([nom, pt]) => `${nom}:${pt[0]},${pt[1]}`).join('|');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${Wpx} ${Hpx}" width="${Wpx}" height="${Hpx}" font-family="Arial" data-figure="${dataFigure}">${s}</svg>`;
};

const elFigure = (scene, cible) => imgElement(rendreScene(scene, cible), 'medium');

/** Tire la NATURE d'abord, puis un quadrilatère de cette nature (équilibre :
 *  sans cela, les trois rectangles de la scène raflent la mise). */
const tirerCible = (scene) => {
    const natures = [...new Set(scene.quads.map((qd) => qd.nature))];
    const nature = randomChoice(natures);
    return randomChoice(scene.quads.filter((qd) => qd.nature === nature));
};

// ─── Format 1 : la nature la plus précise ──────────────────────────────────

// Anti-répétition immédiate (recette standard).
let derniereSignatureNature1079 = null;

const genNatureQcm = () => {
    let payload = construireNatureQcm();
    for (let t = 0; t < 8 && payload.texte + payload.contenu.bonne_reponse === derniereSignatureNature1079; t++) {
        payload = construireNatureQcm();
    }
    derniereSignatureNature1079 = payload.texte + payload.contenu.bonne_reponse;
    return payload;
};

const construireNatureQcm = () => {
    const scene = construireScene();
    const cible = tirerCible(scene);
    const correct = NOMS_NATURES[cible.nature];
    const autres = shuffle(Object.keys(NOMS_NATURES).filter((n) => n !== cible.nature)).slice(0, 3).map((n) => NOMS_NATURES[n]);

    const texte = `<p>Sur la figure, observe le quadrilatère ${cible.nom} (surligné).</p>`
        + `<p>Quelle est sa nature la plus précise ?</p>`;
    const payload = makeQcm([correct, ...autres], correct, texte, {
        ...opts(L_NATURE), elements_additionnels: elFigure(scene, cible.pts),
    });
    payload._meta.params = { nom: cible.nom, nature: cible.nature, W: scene.W, H: scene.H, toit: scene.toit, p: scene.p };
    return payload;
};

// ─── Format 2 : la bonne justification (la nature est donnée) ──────────────

const justificationDe = (scene, cible) => {
    const { nature } = cible;
    if (nature === 'losange') {
        return `De chaque sommet au suivant, même déplacement (${scene.p} carreaux et ${scene.q}) : quatre côtés de même longueur.`;
    }
    // (Le carré incliné KLMN n'existe plus : aucune justification à l'équerre.)
    if (nature === 'carre') {
        return `Ses côtés suivent le quadrillage (quatre angles droits) et ont tous ${scene.H} carreaux.`;
    }
    if (nature === 'rectangle') {
        return `Ses côtés suivent les lignes du quadrillage : ses quatre angles sont droits.`;
    }
    if (nature === 'parallelogramme') {
        return `De K à L comme de N à M, même déplacement (${scene.a} à droite) : côtés opposés parallèles deux à deux.`;
    }
    // trapèze
    return `[DC] et [GH] sont horizontaux, donc parallèles ; les deux autres côtés ne le sont pas.`;
};

// Pièges de justification PAR NATURE (revue 2026-09-02, A2 signalé par le
// prof) : ils parlent le même langage que la bonne réponse (carreaux, lignes
// du quadrillage, déplacements) — plus aucun indice de surface. Chacun est
// soit FAUX pour la figure ciblée, soit INSUFFISANT pour la nature annoncée ;
// jamais correct par construction (la nature ciblée exclut le cas où il le
// serait : un rectangle ciblé n'est jamais carré, un losange KLMN jamais
// aligné sur le quadrillage, un trapèze DGHC jamais à côtés opposés égaux).
const PIEGES_DOCTRINAUX = [
    // L'allure — JAMAIS recevable (la maxime de la fiche).
    'La figure a l\'air très régulière quand on regarde le dessin sur le quadrillage.',
    // La mesure à la règle — n'est plus une preuve en 5e (P10).
    'En mesurant à la règle, ses côtés semblent faire le même nombre de centimètres.',
];

// Gabarits alignés sur les bonnes réponses (80 à 95 caractères) : la longueur
// ne trahit rien (mesure revue 2026-09-02).
const PIEGES_PAR_NATURE = {
    rectangle: [
        'Ses quatre côtés ont le même nombre de carreaux quand on les compte sur le quadrillage.', // faux (jamais carré)
        'Deux de ses côtés ont le même nombre de carreaux, et cela suffit pour conclure.',        // insuffisant
        'Elle possède deux côtés parallèles sur le quadrillage, cela suffit pour conclure.',      // insuffisant (P11)
    ],
    carre: [
        'Ses côtés suivent les lignes du quadrillage : ses quatre angles sont droits.',           // insuffisant (rectangle → carré)
        'Ses quatre côtés ont le même nombre de carreaux, et cela suffit pour conclure.',         // insuffisant (losange seulement)
        'Elle possède deux côtés parallèles sur le quadrillage, cela suffit pour conclure.',      // insuffisant (P11)
    ],
    losange: [
        'Ses côtés suivent les lignes du quadrillage, donc ses quatre angles sont des angles droits.', // faux (côtés obliques)
        'Deux côtés opposés ont le même déplacement de carreaux, et cela suffit pour conclure.',  // insuffisant
        'Ses angles sont droits et ses côtés opposés ont le même nombre de carreaux sur le quadrillage.', // faux
    ],
    trapeze: [
        'Ses côtés opposés sont parallèles deux à deux quand on les suit sur le quadrillage.',    // faux (une seule paire)
        '[DC] et [GH] ont exactement le même nombre de carreaux sur le quadrillage.',             // faux (longueurs différentes)
        'Ses côtés suivent les lignes du quadrillage, donc ses quatre angles sont des angles droits.', // faux
    ],
    parallelogramme: [
        '[KL] et [NM] sont parallèles sur le quadrillage, et cela suffit pour conclure.',         // insuffisant (P11, question d.)
        'Ses quatre côtés ont le même déplacement de carreaux quand on les suit sur le quadrillage.', // faux (côtés inégaux)
        'Ses côtés suivent les lignes du quadrillage, donc ses quatre angles sont des angles droits.', // faux
    ],
};

/** 3 pièges : 2 « quadrillage » de la nature + 1 tiré parmi le 3e et les doctrinaux. */
const tirerPieges = (nature) => {
    const [p1, p2, p3] = shuffle([...PIEGES_PAR_NATURE[nature]]);
    const troisieme = randomChoice([p3, ...PIEGES_DOCTRINAUX]);
    return shuffle([p1, p2, troisieme]);
};

// Anti-répétition immédiate (recette standard).
let derniereSignatureJustifier1079 = null;

const genJustifierQcm = () => {
    let payload = construireJustifierQcm();
    for (let t = 0; t < 8 && payload.texte + payload.contenu.bonne_reponse === derniereSignatureJustifier1079; t++) {
        payload = construireJustifierQcm();
    }
    derniereSignatureJustifier1079 = payload.texte + payload.contenu.bonne_reponse;
    return payload;
};

const construireJustifierQcm = () => {
    const scene = construireScene();
    const cible = tirerCible(scene);
    const bonne = justificationDe(scene, cible);
    const pieges = tirerPieges(cible.nature);
    // Gardes de construction : un piège « faux » ne peut pas être vrai ici.
    if (cible.nature === 'rectangle' && natureQuad(cible.pts) === 'carre') throw new Error('gen_1079 : rectangle ciblé carré');
    if (pieges.includes(bonne)) throw new Error('gen_1079 : un piège coïncide avec la justification');

    const texte = `<p>Sur la figure, ${cible.nom} (surligné) est ${NOMS_NATURES[cible.nature]}.</p>`
        + `<p>Quelle justification est correcte ?</p>`;
    const payload = makeQcm([bonne, ...pieges], bonne, texte, {
        ...opts(L_JUSTIFIER), elements_additionnels: elFigure(scene, cible.pts),
    });
    // Métadonnée DORMANTE (convention gen_1039) : cadence de raisonnement.
    payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
    payload._meta.params = { nom: cible.nom, nature: cible.nature, toit: scene.toit, p: scene.p };
    return payload;
};

// ─── Format 3 : compter les côtés d'un polygone suivi ──────────────────────

const NOMS_POLYGONES = { 3: 'un triangle', 4: 'un quadrilatère', 5: 'un pentagone', 6: 'un hexagone' };

// Anti-répétition immédiate (recette standard).
let derniereSignatureCompter1079 = null;

const genCompterQcm = () => {
    let payload = construireCompterQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureCompter1079; t++) {
        payload = construireCompterQcm();
    }
    derniereSignatureCompter1079 = payload.texte;
    return payload;
};

const construireCompterQcm = () => {
    const scene = construireScene();
    // Vivier de chemins élargi (revue 2026-09-02, A8) : triangles, quadrilatères,
    // pentagones, hexagone — tous à sommets RÉELS (jamais un point aligné qui
    // ferait un faux côté), les quatre réponses peuvent être correctes.
    const Pp = scene.P;
    const chemin3 = (noms) => ({ noms, pts: noms.map((n) => Pp[n]) });
    const chemins = [
        { noms: scene.contour.noms, pts: scene.contour.pts },
        chemin3(['A', 'E', 'F', 'D']),
        chemin3(['E', 'B', 'C', 'F']),
        chemin3(['A', 'E', 'F']),
    ];
    if (scene.toit === 'pointe') {
        // (D → S → C → F exclu : F est aligné sur [DC], ce serait un faux quadrilatère.)
        chemins.push(chemin3(['D', 'S', 'C']), chemin3(['A', 'B', 'S']), chemin3(['E', 'B', 'C', 'S', 'D']));
    } else {
        chemins.push(
            chemin3(['B', 'C', 'H', 'G', 'D']),
            chemin3(['E', 'B', 'C', 'H', 'G']),
            chemin3(['A', 'B', 'H', 'G', 'D']),
            chemin3(['A', 'B', 'H', 'G']),
            chemin3(['E', 'B', 'C', 'H']),
            chemin3(['D', 'G', 'F']),
        );
    }
    // GARDE : un sommet aligné avec ses deux voisins n'est pas un vrai coin —
    // le chemin serait un faux polygone (ex. H sur la droite (CE) pour
    // certaines dimensions du toit-parallélogramme). Recompté par produit en
    // croix, chemin écarté.
    const sansAlignement = (pts) => pts.every((P1, k) => {
        const P0 = pts[(k + pts.length - 1) % pts.length]; const P2 = pts[(k + 1) % pts.length];
        return cross(vec(P0, P1), vec(P1, P2)) !== 0;
    });
    const chemin = randomChoice(chemins.filter((c) => sansAlignement(c.pts)));
    const n = chemin.pts.length;
    const correct = NOMS_POLYGONES[n];
    const autres = shuffle(Object.keys(NOMS_POLYGONES).map(Number).filter((x) => x !== n)).slice(0, 3).map((x) => NOMS_POLYGONES[x]);

    const texte = `<p>Sur la figure, on suit le contour ${chemin.noms.join(' → ')} → ${chemin.noms[0]} (surligné).</p>`
        + `<p>Comment se nomme ce polygone ?</p>`;
    const payload = makeQcm([correct, ...autres], correct, texte, {
        ...opts(L_COMPTER), elements_additionnels: elFigure(scene, chemin.pts),
    });
    payload._meta.params = { chemin: chemin.noms.join(''), n, toit: scene.toit };
    return payload;
};

// ─── Format 4 : V/F recalculé sur la figure du tirage ──────────────────────

// Anti-répétition immédiate (recette standard).
let derniereSignatureVf1079 = null;

const genFigureVf = () => {
    let payload = construireFigureVf();
    for (let t = 0; t < 8 && JSON.stringify(payload.contenu) === derniereSignatureVf1079; t++) {
        payload = construireFigureVf();
    }
    derniereSignatureVf1079 = JSON.stringify(payload.contenu);
    return payload;
};

const construireFigureVf = () => {
    for (let essai = 0; essai < 30; essai++) {
        const scene = construireScene();
        const toitQuad = scene.quads.find((qd) => qd.nom === 'DGHC');
        // Candidats à vérité VARIABLE, recalculée sur la scène. Jumelles
        // exclusives (même savoir) regroupées : une seule par tirage.
        const groupes = [
            [{ l: 'AEFD est un carré.', v: scene.W / 2 === scene.H },
                { l: 'AEFD est un rectangle.', v: true }],
            [{ l: 'ABCD est un carré.', v: false },
                { l: 'ABCD est un rectangle.', v: true }],
            // « KLMN est un carré » : toujours faux désormais — le piège P11
            // du losange pris pour un carré (déplacements 2-1 ou 3-1).
            [{ l: 'KLMN est un carré.', v: false },
                { l: 'KLMN a ses quatre côtés de même longueur.', v: scene.variante === 'losange' },
                { l: 'KLMN est un losange.', v: scene.variante === 'losange' }],
            [{ l: 'EBCF est un rectangle.', v: true }],
            [{ l: 'Le contour extérieur de la figure est un hexagone.', v: scene.toit !== 'pointe' },
                { l: 'Le contour extérieur de la figure est un pentagone.', v: scene.toit === 'pointe' }],
        ];
        if (toitQuad) {
            groupes.push([
                { l: 'DGHC est un parallélogramme.', v: toitQuad.nature === 'parallelogramme' },
                { l: 'DGHC a deux paires de côtés parallèles.', v: toitQuad.nature === 'parallelogramme' },
                // « DGHC est un trapèze » : posée UNIQUEMENT quand c'est vrai au
                // sens strict (l'inclusif parallélogramme ⊂ trapèze est ambigu).
                ...(toitQuad.nature === 'trapeze' ? [{ l: 'DGHC est un trapèze.', v: true }] : []),
            ]);
        }
        const tirage = shuffle(groupes.map((grp) => randomChoice(grp))).slice(0, 4);
        const nbFaux = tirage.filter((it) => !it.v).length;
        if (nbFaux < 1 || nbFaux > 3) continue; // quota variable, jamais uniforme

        const payload = makeVraiFaux(
            tirage.map((it) => it.l),
            tirage.map((it) => (it.v ? 0 : 1)),
            '<p>Observe la figure, puis dis si chaque affirmation est vraie ou fausse.</p>',
            { ...opts(L_VF), elements_additionnels: elFigure(scene, null) },
        );
        // Métadonnée DORMANTE (convention gen_1039) : cadence de raisonnement.
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { W: scene.W, H: scene.H, p: scene.p, toit: scene.toit };
        return payload;
    }
    throw new Error('gen_1079 : tirage V/F sans mixité après 30 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    nature_qcm_unique: genNatureQcm,
    justification_qcm_unique: genJustifierQcm,
    polygone_qcm_unique: genCompterQcm,
    figure_tableau_vf: genFigureVf,
};

const FORMAT_LABELS = { qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((kk) => kk.startsWith(`${prefix}_`))
        .map((kk) => ({ key: kk, label: FORMAT_LABELS[kk.slice(prefix.length + 1)] || kk }));

export const CATEGORIES = [
    { id: 'nature', label: L_NATURE, formatsList: buildFormats('nature') },
    { id: 'justification', label: L_JUSTIFIER, formatsList: buildFormats('justification') },
    { id: 'polygone', label: L_COMPTER, formatsList: buildFormats('polygone') },
    { id: 'figure', label: L_VF, formatsList: buildFormats('figure') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((kk) => kk.startsWith(typeExercice) || kk.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
